/**
 * Match narrative + pre-game brief generators.
 *
 * - generateMatchNarrative: post-match 3-4 sentence narrative (spec §08.2)
 *   pulls game + top/low-graded players, asks Claude for a report the
 *   coach can drop into games.match_report. Editable before save.
 *
 * - generatePreMatchBrief: pre-game 3-4 sentence tactical focus pulling
 *   game + team philosophy + last game vs same opponent + current
 *   curriculum focus. Drops into games.pregame_brief. Same editable flow.
 */
import { callAI } from './ai';
import { supabase } from './supabase';

export interface MatchNarrativeArgs {
  gameId: string;
  coachId: string;
}

export interface MatchNarrativeResult {
  ok: boolean;
  text?: string;
  error?: string;
}

const NARRATIVE_SYSTEM = `You are a soccer coach writing a brief post-match report for the
coach's own records + parent communication. Use growth-mindset language
(effort and process, not innate ability). Keep it encouraging but honest.

Return plain text, 3–4 sentences. No markdown, no bullet lists, no headers.`;

const BRIEF_SYSTEM = `You are a soccer coach writing a concise pre-match tactical brief for
your team. Reference the upcoming opponent and the team's own playing
principles. Use growth-mindset language. Be specific, actionable, and
age-appropriate.

Return plain text, 3–4 sentences. No markdown, no bullet lists, no headers.`;

export async function generateMatchNarrative(args: MatchNarrativeArgs): Promise<MatchNarrativeResult> {
  const { data: game, error } = await supabase
    .from('games')
    .select('id, team_id, opponent, date, competition, home_away, venue, result, opponent_notes_pre, opponent_notes_post, teams(name, age_group)')
    .eq('id', args.gameId)
    .maybeSingle();

  if (error) return { ok: false, error: error.message };
  if (!game) return { ok: false, error: 'Game not found' };

  const g = game as any;
  const team = Array.isArray(g.teams) ? g.teams[0] : g.teams;

  // Pull coach's own ratings for this game + the player names, so the
  // narrative can name specific players per spec §08.2 ("top-rated
  // players: …", "areas of concern: …").
  const { data: ratings } = await supabase
    .from('ratings')
    .select('ratee_id, corner_id, score, users!ratings_ratee_id_fkey(name)')
    .eq('rater_id', args.coachId)
    .eq('ratee_type', 'PLAYER')
    .eq('context', 'GAME')
    .eq('game_id', args.gameId);

  // Average score per player across corners.
  const perPlayer = new Map<string, { name: string; scores: number[] }>();
  for (const r of (ratings ?? []) as any[]) {
    const name = Array.isArray(r.users) ? r.users[0]?.name : r.users?.name;
    if (!name) continue;
    const bucket = perPlayer.get(r.ratee_id) ?? { name, scores: [] as number[] };
    if (typeof r.score === 'number') bucket.scores.push(r.score);
    perPlayer.set(r.ratee_id, bucket);
  }
  const avgs = Array.from(perPlayer.entries())
    .map(([, v]) => ({ name: v.name, avg: v.scores.length ? v.scores.reduce((s, n) => s + n, 0) / v.scores.length : 0 }))
    .filter(x => x.avg > 0)
    .sort((a, b) => b.avg - a.avg);
  const top = avgs.slice(0, 3).map(x => `${x.name.split(' ')[0]} (${Math.round(x.avg)})`);
  const low = avgs.slice(-2).filter(x => !top.some(t => t.startsWith(x.name.split(' ')[0]))).map(x => x.name.split(' ')[0]);

  const lines: string[] = [];
  lines.push(`Match context:`);
  lines.push(`- Team: ${team?.name ?? '(unknown)'}${team?.age_group ? ` (${team.age_group})` : ''}`);
  lines.push(`- Competition: ${g.competition}`);
  lines.push(`- Opponent: ${g.opponent}`);
  lines.push(`- Date: ${g.date}`);
  lines.push(`- Home / Away: ${g.home_away}`);
  if (g.venue) lines.push(`- Venue: ${g.venue}`);
  if (g.result) lines.push(`- Result: ${g.result}`);
  if (top.length) lines.push(`- Top-rated players: ${top.join(', ')}`);
  if (low.length) lines.push(`- Players to watch (development): ${low.join(', ')}`);
  if (g.opponent_notes_pre) lines.push(`\nPre-game opponent notes (what we expected):\n${g.opponent_notes_pre}`);
  if (g.opponent_notes_post) lines.push(`\nPost-game observations (what we saw):\n${g.opponent_notes_post}`);

  lines.push(`\nWrite a 3–4 sentence post-match narrative:`);
  lines.push(`1. Reference at least one 4-corner dimension (Technical/Tactical, Physical, Psychological, Social)`);
  lines.push(`2. Identify one strength the team showed${top.length ? ` — you may name one of the top-rated players` : ''}`);
  lines.push(`3. Identify one development focus for the next cycle`);
  lines.push(`4. Use encouraging, development-focused language — age-appropriate for ${team?.age_group ?? 'youth'} players + their parents`);

  const text = await callAI({
    system: NARRATIVE_SYSTEM,
    userContent: lines.join('\n'),
    maxTokens: 400,
    surface: 'session-review-narrative',
  });

  if (!text) return { ok: false, error: 'AI returned empty response' };
  return { ok: true, text: text.trim() };
}

export async function generatePreMatchBrief(args: MatchNarrativeArgs): Promise<MatchNarrativeResult> {
  const { data: game, error } = await supabase
    .from('games')
    .select('id, team_id, opponent, date, competition, home_away, venue, opponent_notes_pre, teams(name, age_group, formation_default)')
    .eq('id', args.gameId)
    .maybeSingle();

  if (error) return { ok: false, error: error.message };
  if (!game) return { ok: false, error: 'Game not found' };

  const g = game as any;
  const team = Array.isArray(g.teams) ? g.teams[0] : g.teams;
  const teamId: string | undefined = g.team_id;

  // Team philosophy principles — grouped by type.
  const { data: principles } = teamId
    ? await supabase
        .from('principles')
        .select('type, text')
        .eq('team_id', teamId)
        .is('deleted_at', null)
    : { data: null };

  const byType: Record<string, string[]> = {};
  for (const p of (principles ?? []) as any[]) {
    (byType[p.type] ??= []).push(p.text);
  }

  // Last game vs the same opponent + result (for continuity).
  const { data: lastVsOpp } = teamId
    ? await supabase
        .from('games')
        .select('date, result, opponent_notes_post, match_report')
        .eq('team_id', teamId)
        .eq('opponent', g.opponent)
        .lt('date', g.date)
        .order('date', { ascending: false })
        .limit(1)
    : { data: null };

  // Current curriculum focus for the week (if one exists).
  const { data: curriculum } = teamId
    ? await supabase
        .from('curriculum_plans')
        .select('plan_data')
        .eq('team_id', teamId)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle()
    : { data: null };
  const currentTopic = pickCurrentTopic(curriculum?.plan_data, g.date);

  const lines: string[] = [];
  lines.push(`Upcoming match:`);
  lines.push(`- Team: ${team?.name ?? '(unknown)'}${team?.age_group ? ` (${team.age_group})` : ''}`);
  lines.push(`- Formation: ${team?.formation_default ?? 'not set'}`);
  lines.push(`- Competition: ${g.competition}`);
  lines.push(`- Opponent: ${g.opponent}`);
  lines.push(`- Date: ${g.date}`);
  lines.push(`- Home / Away: ${g.home_away}`);
  if (g.venue) lines.push(`- Venue: ${g.venue}`);

  if (Object.keys(byType).length) {
    lines.push(`\nOur principles:`);
    for (const [type, texts] of Object.entries(byType)) {
      lines.push(`- ${type}: ${texts.slice(0, 3).join('; ')}`);
    }
  }
  if (currentTopic) {
    lines.push(`\nThis week's curriculum focus: ${currentTopic}`);
  }
  if (g.opponent_notes_pre) {
    lines.push(`\nCoach's opponent research:\n${g.opponent_notes_pre}`);
  }
  if (lastVsOpp && lastVsOpp.length > 0) {
    const prev = lastVsOpp[0] as any;
    const bits = [`date ${prev.date}`];
    if (prev.result) bits.push(`result ${prev.result}`);
    if (prev.opponent_notes_post) bits.push(`we noted: ${prev.opponent_notes_post.slice(0, 200)}`);
    lines.push(`\nLast time we played ${g.opponent}: ${bits.join(' · ')}`);
  }

  lines.push(`\nWrite a 3–4 sentence pre-match tactical brief:`);
  lines.push(`1. Name a specific tactical key for the first 10 minutes`);
  lines.push(`2. Reference one of our own principles we want to execute`);
  lines.push(`3. If we have research on ${g.opponent}, flag one threat to contain`);
  lines.push(`4. Use encouraging language — we're preparing, not scaring the team`);

  const text = await callAI({
    system: BRIEF_SYSTEM,
    userContent: lines.join('\n'),
    maxTokens: 400,
    surface: 'session-review-narrative',
  });

  if (!text) return { ok: false, error: 'AI returned empty response' };
  return { ok: true, text: text.trim() };
}

/** Find the weekly topic that covers `date` from a curriculum plan_data. */
function pickCurrentTopic(planData: unknown, date: string): string | null {
  if (!planData || typeof planData !== 'object') return null;
  const phases = (planData as any).phase_plans;
  if (!Array.isArray(phases)) return null;
  for (const phase of phases) {
    for (const wk of phase.weekly_topics ?? []) {
      if (wk.date_start && wk.date_end && date >= wk.date_start && date <= wk.date_end) {
        return wk.title ?? wk.topic ?? null;
      }
    }
  }
  return null;
}
